// Build a CSV string: a header row then one row per record, every cell quoted so commas, quotes and newlines survive Excel.
export function toCsv(headers, rows) {
  const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [headers, ...rows].map((r) => r.map(cell).join(",")).join("\r\n");
}

// Save `text` as a CSV download; the BOM makes Excel read UTF-8 names correctly.
export function downloadCsv(filename, text) {
  const blob = new Blob(["﻿", text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// "Head of Treasury (HK)" → "head-of-treasury-hk-2026-10-27.csv"
export function csvFileName(label) {
  const slug = String(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
  const day = new Date().toISOString().slice(0, 10);
  return `${slug || "candidates"}-${day}.csv`;
}

// A candidate's location as text: sourced docs carry { original_location, modified_location }, others a plain string.
export function locationCells(location, country) {
  if (location && typeof location === "object") {
    const place = String(location.original_location || "").trim();
    const ctry = String(location.modified_location || "").trim();
    return { place: place || ctry, country: ctry };
  }
  return { place: String(location || "").trim(), country: String(country || "").trim() };
}

// "2020-01" + "2022-06" (or current) → "2020-01 – 2022-06" / "2020-01 – Present"
function ymRange(start, end, current) {
  const s = String(start || "").slice(0, 7);
  const e = current ? "Present" : String(end || "").slice(0, 7);
  return [s, e].filter(Boolean).join(" – ");
}

// {year, month} or "2020-01" → "2020"
function yearOf(v) {
  if (v && typeof v === "object") return v.year ? String(v.year) : "";
  return String(v || "").slice(0, 4);
}

const LANGUAGE_PROFICIENCY = {
  NATIVE_OR_BILINGUAL: "Native or bilingual proficiency",
  FULL_PROFESSIONAL: "Full professional proficiency",
  PROFESSIONAL_WORKING: "Professional working proficiency",
  LIMITED_WORKING: "Limited working proficiency",
  ELEMENTARY: "Elementary proficiency",
};

// LinkedIn's proficiency enum ("FULL_PROFESSIONAL") as words; human-written values pass through.
export function formatLanguageProficiency(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const key = raw.toUpperCase().replace(/[\s-]+/g, "_");
  if (LANGUAGE_PROFICIENCY[key]) return LANGUAGE_PROFICIENCY[key];
  if (!/^[A-Z0-9_]+$/.test(raw)) return raw;
  const words = key.toLowerCase().replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// The profile sections as one cell each, entries separated by " | " so cells stay one line; `proficiency` turns LinkedIn's enum into words.
export function profileCells(pp, proficiency = (x) => x) {
  const p = pp && typeof pp === "object" ? pp : {};
  const experience = (p.employment_history || [])
    .map((h) => {
      const role = [h.title, h.organization_name].filter(Boolean).join(" — ");
      const when = ymRange(h.start_date, h.end_date, h.current);
      return role ? (when ? `${role} (${when})` : role) : "";
    })
    .filter(Boolean)
    .join(" | ");
  const education = (p.educations || [])
    .map((ed) => {
      const school = ed.schoolName || ed.school?.name || "";
      const what = [ed.degree, ed.fieldOfStudy].filter(Boolean).join(", ");
      const years = [yearOf(ed.start), yearOf(ed.end)].filter(Boolean).join(" – ");
      const head = [what, school].filter(Boolean).join(" — ");
      return head ? (years ? `${head} (${years})` : head) : "";
    })
    .filter(Boolean)
    .join(" | ");
  const skills = (p.skills || []).map((s) => (typeof s === "string" ? s : s?.name || "")).filter(Boolean).join(", ");
  const languages = (p.languages || [])
    .map((l) => {
      if (typeof l === "string") return l;
      const name = l?.name || l?.language || "";
      const level = proficiency(l?.proficiency || "");
      return name ? (level ? `${name} (${level})` : name) : "";
    })
    .filter(Boolean)
    .join(", ");
  const certifications = (p.certifications || [])
    .map((c) => (typeof c === "string" ? c : [c?.name, c?.authority].filter(Boolean).join(" — ")))
    .filter(Boolean)
    .join(" | ");
  const summary = String(p.summary || "").replace(/\s*\n\s*/g, " ").trim();
  return { experience, education, skills, languages, certifications, summary };
}
